// Biểu đồ xu hướng 12 tháng cho từng nhóm, tô nền các tháng thuộc kỳ đang xem.
import { Bar, CartesianGrid, ComposedChart, Line, ReferenceArea, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { DEPT, fmtMoney, type Range } from './insights';
import type { YearTrends } from './useYearTrends';
import { PALETTE, Panel } from './ui';

const MONTHS = Array.from({ length: 12 }, (_, i) => `T${i + 1}`);
const axis = { tickLine: false, axisLine: false, tick: { fontSize: 11, fill: '#64748b' } };

/** Vùng tháng của kỳ đang xem (chỉ khi kỳ nằm trong năm đang vẽ). Gọi như hàm vì recharts chỉ nhận ReferenceArea là con trực tiếp. */
const highlight = (range: Range, year: number, yAxisId?: string) => {
  if (range.end.year() !== year) return null;
  const from = range.start.year() === year ? range.start.month() : 0;
  return <ReferenceArea yAxisId={yAxisId} x1={MONTHS[from]} x2={MONTHS[range.end.month()]} fill="#94a3b8" fillOpacity={0.12} ifOverflow="extendDomain" />;
};

const Legend = ({ items }: { items: { label: string; color: string; line?: boolean }[] }) => (
  <div className="mb-1 flex flex-wrap gap-3 text-xs text-slate-500">
    {items.map(i => (
      <span key={i.label} className="flex items-center gap-1.5">
        {i.line ? <span className="w-4 border-t-2" style={{ borderColor: i.color }} /> : <span className="h-2.5 w-2.5 rounded-sm" style={{ background: i.color }} />}
        {i.label}
      </span>
    ))}
  </div>
);

const Wrap = ({ title, info, children, loading, empty }: { title: string; info?: string; children: React.ReactNode; loading: boolean; empty: boolean }) => (
  <Panel title={title} info={info}>
    {loading ? <div className="flex h-[220px] items-center justify-center text-sm text-slate-400">Đang tải…</div>
      : empty ? <div className="flex h-[220px] items-center justify-center text-sm text-slate-400">Chưa có dữ liệu năm này</div>
        : children}
  </Panel>
);

export function PageTrend({ t, range }: { t: YearTrends; range: Range }) {
  const data = MONTHS.map((m, i) => {
    const r = t.page.find(x => Number(x.month) === i + 1);
    return { m, leads: Number(r?.total_new_customers) || 0, done: Number(r?.total_completed_appointments) || 0 };
  });
  return (
    <Wrap title={`Xu hướng ${t.year}`} info="Khách hàng mới và lịch hẹn hoàn thành theo tháng. Vùng tô xám là kỳ đang xem." loading={t.loading} empty={!data.some(d => d.leads || d.done)}>
      <Legend items={[{ label: 'Khách hàng mới', color: DEPT.page.color }, { label: 'Lịch hẹn hoàn thành', color: '#1D9E75', line: true }]} />
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 0, left: -20, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#eef2f5" />
            <XAxis dataKey="m" {...axis} />
            <YAxis yAxisId="l" allowDecimals={false} {...axis} />
            <YAxis yAxisId="r" orientation="right" allowDecimals={false} {...axis} />
            {highlight(range, t.year, "l")}
            <ChartTooltip formatter={(v, k) => [Number(v).toLocaleString('vi-VN'), k === 'leads' ? 'Khách hàng mới' : 'Lịch hẹn hoàn thành']} />
            <Bar yAxisId="l" dataKey="leads" fill={DEPT.page.color} radius={[3, 3, 0, 0]} maxBarSize={22} />
            <Line yAxisId="r" dataKey="done" stroke="#1D9E75" strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Wrap>
  );
}

export function TeleTrend({ t, range }: { t: YearTrends; range: Range }) {
  const data = MONTHS.map((m, i) => {
    const r = t.tele[String(i + 1)];
    return { m, new: Number(r?.new) || 0, success: Number(r?.success) || 0, canceled: Number(r?.canceled) || 0 };
  });
  return (
    <Wrap title={`Lịch hẹn theo tháng ${t.year}`} info="Lịch hẹn mới, thành công và hủy theo tháng. Vùng tô xám là kỳ đang xem." loading={t.loading} empty={!data.some(d => d.new || d.success || d.canceled)}>
      <Legend items={[{ label: 'Lịch mới', color: DEPT.tele.color }, { label: 'Thành công', color: '#1D9E75' }, { label: 'Hủy', color: '#E24B4A', line: true }]} />
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#eef2f5" />
            <XAxis dataKey="m" {...axis} />
            <YAxis allowDecimals={false} {...axis} />
            {highlight(range, t.year)}
            <ChartTooltip formatter={(v, k) => [Number(v).toLocaleString('vi-VN'), k === 'new' ? 'Lịch mới' : k === 'success' ? 'Thành công' : 'Hủy']} />
            <Bar dataKey="new" fill={DEPT.tele.color} radius={[3, 3, 0, 0]} maxBarSize={16} />
            <Bar dataKey="success" fill="#1D9E75" radius={[3, 3, 0, 0]} maxBarSize={16} />
            <Line dataKey="canceled" stroke="#E24B4A" strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Wrap>
  );
}

export function SaleTrend({ t, range }: { t: YearTrends; range: Range }) {
  const branches = t.sale?.branches || [];
  const data = MONTHS.map((m, i) => {
    const row: Record<string, number | string> = { m, revenue: Number(t.sale?.revenue_s8?.[i]) || 0 };
    branches.forEach(b => { row[`b${b.id}`] = Number(t.sale?.[`success_branch_${b.id}`]?.[i]) || 0; });
    return row;
  });
  const empty = !data.some(d => Number(d.revenue) || branches.some(b => Number(d[`b${b.id}`])));
  return (
    <Wrap title={`Theo tháng ${t.year}`} info="Số ca thành công (S6 trở lên) theo chi nhánh và doanh thu nhóm khách chốt. Vùng tô xám là kỳ đang xem." loading={t.loading} empty={empty}>
      <Legend items={[...branches.map((b, i) => ({ label: b.name, color: PALETTE[i % PALETTE.length] })), { label: 'Doanh thu', color: '#EF9F27', line: true }]} />
      <div className="h-[220px]">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 0, left: -20, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#eef2f5" />
            <XAxis dataKey="m" {...axis} />
            <YAxis yAxisId="l" allowDecimals={false} {...axis} />
            <YAxis yAxisId="r" orientation="right" {...axis} tickFormatter={v => fmtMoney(Number(v))} />
            {highlight(range, t.year, "l")}
            <ChartTooltip formatter={(v, k) => (k === 'revenue' ? [fmtMoney(Number(v)), 'Doanh thu'] : [`${v} ca`, branches.find(b => `b${b.id}` === k)?.name || String(k)])} />
            {branches.map((b, i) => <Bar key={b.id} yAxisId="l" dataKey={`b${b.id}`} stackId="s" fill={PALETTE[i % PALETTE.length]} maxBarSize={22} />)}
            <Line yAxisId="r" dataKey="revenue" stroke="#EF9F27" strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Wrap>
  );
}
